// react_frontend/src/Components/Modules/UserSetting/UserList.js
import React, { useContext, useMemo, useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FaUserShield, FaUserTie, FaCashRegister, FaUser, FaEdit, FaTrashAlt, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { MdOutlineSearch } from "react-icons/md";
import {
  Button,
  Input,
  Badge,
  Loader,
  Tooltip,
  Whisper,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { MotherContext } from "../../../Context/MotherContext";

// Helper: Format role label
const getRoleLabel = (whoAmI) => {
  switch (whoAmI) {
    case 1:
      return { label: "Master Admin", color: "purple", icon: <FaUserShield /> };
    case 2:
      return { label: "Admin", color: "blue", icon: <FaUserTie /> };
    case 3:
      return { label: "Account", color: "cyan", icon: <FaCashRegister /> };
    default:
      return { label: "Sales Manager", color: "green", icon: <FaUser /> };
  }
};

// Action Buttons
const ActionButtons = ({ user, onDelete, onStatusToggle }) => {
  const currentUserId = localStorage.getItem("id");
  const currentUserRole = parseInt(localStorage.getItem("who_am_i"));
  const targetUserId = user.id;
  const targetUserRole = parseInt(user.who_am_i);

  const canEdit =
    currentUserRole === 1 ||
    (currentUserRole === 2 && targetUserRole > 1 && currentUserId !== targetUserId);

  if (targetUserRole === 1 && currentUserRole !== 1) {
    return (
      <Whisper placement="top" speaker={<Tooltip>Restricted Access</Tooltip>}>
        <Badge color="gray">Restricted</Badge>
      </Whisper>
    );
  }

  return (
    <div className="d-flex gap-1 justify-content-center">
      {canEdit ? (
        <>
          {/* Edit */}
          <Whisper placement="top" speaker={<Tooltip>Edit user details</Tooltip>}>
            <Link to={`/edit_user/${targetUserId}`} style={{ textDecoration: "none" }}>
              <Button size="sm" color="blue" startIcon={<FaEdit />}>
                Edit
              </Button>
            </Link>
          </Whisper>

          {/* Status Toggle */}
          <Whisper
            placement="top"
            speaker={
              <Tooltip>
                {user.user_status ? "Deactivate user" : "Activate user"}
              </Tooltip>
            }
          >
            <Button
              size="sm"
              color={user.user_status ? "green" : "red"}
              startIcon={user.user_status ? <FaToggleOn /> : <FaToggleOff />}
              onClick={() => onStatusToggle(user)}
            >
              {user.user_status ? "Active" : "Inactive"}
            </Button>
          </Whisper>

          {/* Delete (only Master Admin) */}
          {currentUserRole === 1 && (
            <Whisper placement="top" speaker={<Tooltip>Delete user permanently</Tooltip>}>
              <Button
                size="sm"
                color="red"
                startIcon={<FaTrashAlt />}
                onClick={() => onDelete(user)}
              >
                Delete
              </Button>
            </Whisper>
          )}
        </>
      ) : (
        <Whisper placement="top" speaker={<Tooltip>You cannot edit yourself</Tooltip>}>
          <Badge color="default">Self</Badge>
        </Whisper>
      )}
    </div>
  );
};

const UserList = () => {
  const {
    users,
    removeUserFromList,
    updateUserInList,
    startUserPolling,   // ✅ from context
    stopUserPolling     // ✅ from context
  } = useContext(MotherContext);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // ✅ Start polling only when this page is mounted
  useEffect(() => {
    startUserPolling(); // page mount
    return () => stopUserPolling(); // page leave
  }, [startUserPolling, stopUserPolling]);

  // Filtered Data
  const filteredData = useMemo(() => {
    return users.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.email.toLowerCase().includes(search.toLowerCase()) ||
      item.phone?.toLowerCase().includes(search.toLowerCase()) ||
      getRoleLabel(item.who_am_i).label.toLowerCase().includes(search.toLowerCase())
    );
  }, [users, search]);

  // Handle Delete
  const handleDelete = (user) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this user!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.delete(`/users/${user.id}`);
          removeUserFromList(user.id);
          Swal.fire("Deleted!", "User has been deleted.", "success");
        } catch (err) {
          Swal.fire("Error", "Failed to delete user.", "error");
        }
      }
    });
  };

  // Handle Status Toggle
  const handleStatusToggle = (user) => {
    const newStatus = user.user_status === 1 ? 0 : 1;
    Swal.fire({
      title: `Are you sure?`,
      text: `You will mark this user as ${newStatus ? "Active" : "Inactive"}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${newStatus ? "Activate" : "Deactivate"}`,
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.post(`/update_user_status/${user.id}/${newStatus}`);
          const updatedUser = { ...user, user_status: newStatus };
          updateUserInList(updatedUser);
          Swal.fire("Updated!", "User status has been changed.", "success");
        } catch (err) {
          Swal.fire("Error", "Failed to update status.", "error");
        }
      }
    });
  };

  // Columns
  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "60px",
      center: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) => <div className="fw-medium text-dark">{row.name}</div>,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      grow: 2,
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "—",
      sortable: true,
      width: "140px",
    },
    {
      name: "Role",
      cell: (row) => {
        const role = getRoleLabel(row.who_am_i);
        return (
          <Badge
            className="d-flex align-items-center gap-1 px-2 py-1"
            color={role.color}
            style={{ fontSize: "0.85em" }}
          >
            {role.icon}
            <span>{role.label}</span>
          </Badge>
        );
      },
      sortable: true,
      width: "180px",
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`badge ${row.user_status ? "bg-success" : "bg-secondary"}`}>
          {row.user_status ? "Active" : "Inactive"}
        </span>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionButtons
          user={row}
          onDelete={handleDelete}
          onStatusToggle={handleStatusToggle}
        />
      ),
      width: "300px",
      center: true,
    },
  ];

  // Custom Styling
  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "50px",
      },
    },
    cells: {
      style: {
        padding: "10px 8px",
      },
    },
  };

  const AddUserButton = () => (
    <Whisper placement="top" speaker={<Tooltip>Add a new user</Tooltip>}>
      <Link to="/add_user" style={{ textDecoration: "none" }}>
        <Button
          appearance="primary"
          startIcon={<FaUser />}
          className="d-flex align-items-center custom-bg"
        >
          Add User
        </Button>
      </Link>
    </Whisper>
  );

  return (
    <>
      <Breadcrumb title="User Management" />

      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <i className="fas fa-users me-2"></i>
          Users
        </h5>

        <div className="d-flex gap-2 w-100 w-md-auto">
          <Input
            placeholder="Search by name, email, phone..."
            value={search}
            onChange={setSearch}
            prefix={<MdOutlineSearch />}
            style={{ width: 300 }}
            size="md"
          />
          {localStorage.getItem("who_am_i") === "1" && <AddUserButton />}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 250, 500, 1000]}
        progressPending={loading}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading users...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <h5>No users found</h5>
            <p>Try adjusting your search or add a new user.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />
    </>
  );
};

export default UserList;
